import { ref, toValue } from 'vue'
import { createCalculationConfig } from '@/config/calculation.config.ts'
import type { Dependable } from '@/ts/types/dependable.type.ts'
import { type FormField } from '@/ts/types/schema.types'
import { useCalculationStore } from '@/stores/calculationStore.ts'
import { useMappingStore } from '@/stores/mappingStore.ts'
import { useGlobalStore } from '@/stores/globalStore.ts'
import { isNotNullish, isNullish } from '@/helpers/type-checks.ts'
import { type FormFieldMappingKey } from '@/ts/types/form-field-mapping-key.ts'
import type {
  CalculationDependencies,
  CalculationDependencyConfig,
  CalculationDependencyConfigItem,
  CalculationExternalDependencies,
  CalculationFunctionParams,
  CalculationKey,
  CalculationResults,
  FormFieldInformation,
  FormFieldValues,
  OptionalFormFieldMappingRelated
} from '@/ts/types/calculation-config.type.ts'
import { logWarn } from '@/helpers/logging.helpers.ts'
import { executeCalculationFnOfCalculationKey } from '@/helpers/calculation-function.helpers.ts'
import { BmfApiError } from '@/ts/classes/errors/bmf-api.error.ts'
import { CalculationError } from '@/ts/classes/errors/calculation.error.ts'
import { validateField } from '@/helpers/execute-field-rules.helper.ts'

type CalculationExecutionInfos = {
  calculations: CalculationKey[]
  calculationDependencies: CalculationKey[]
  fieldDependencies: FormFieldMappingKey[]
  configDependencies: string[]
}

type CalculationServicePatchInfo = {
  calculationResults: { [Key in CalculationKey]?: CalculationResultValue }
}

type CalculationResultValue = string | number | boolean | null

type CalculationServiceExecuteCalculationsResult = { calculationResults: CalculationResults }

// Configuration as global state so every instance of the service can use it
const calculationDependencyConfig = ref<(CalculationDependencyConfig & Dependable) | null>(null)

// Queue of fields which changed
const calculationQueue: FormFieldMappingKey[] = []

// Field id which dependants are currently calculating
let currentCalculationExecution: FormFieldMappingKey | null = null

let currentError: CalculationError | null = null

// Function for updating the global state of calculationDependencyConfig and resolving dependents of each calculation
function updateCalculationConfig(calculationConfig: CalculationDependencyConfig) {
  if (!calculationConfig) calculationDependencyConfig.value = null
  const entries = Object.entries(calculationConfig)

  const calculationDependencyConfigWithFirstLevelDependants: CalculationDependencyConfig & Dependable = entries.reduce(
    (result, [key, value]) => ({
      ...result,
      [key]: {
        ...value,
        dependants: entries.flatMap(([dependantKey, dependantValue]) =>
          dependantValue.dependsOn?.calculation?.includes(key as CalculationKey) ? [dependantKey] : []
        )
      }
    }),
    {}
  )

  calculationDependencyConfig.value = Object.entries(calculationDependencyConfigWithFirstLevelDependants).reduce((result, [key, value]) => {
    return {
      ...result,
      [key]: {
        ...value,
        dependants: getCalculationDependants(key as CalculationKey, calculationDependencyConfigWithFirstLevelDependants)
      }
    }
  }, {})
}

function getCalculationDependants(calculationKey: CalculationKey, dependables: Dependable): CalculationKey[] {
  const directDependants = dependables[calculationKey]?.dependants
  if (!directDependants?.length) return []
  return [...new Set([...directDependants, ...directDependants.flatMap((dependant) => getCalculationDependants(dependant, dependables))])]
}

// composable for calculation service
function createCalculationService() {
  // updating calculation config only if there isn't already one
  if (!calculationDependencyConfig.value) updateCalculationConfig(createCalculationConfig())

  // function for merging CalculationExecutionInfo objects
  function combineCalculationExecutionInfos(...calculationExecutionInfos: CalculationExecutionInfos[]): CalculationExecutionInfos {
    const result: CalculationExecutionInfos = {
      calculations: [],
      calculationDependencies: [],
      configDependencies: [],
      fieldDependencies: []
    }

    for (const { calculations, calculationDependencies, configDependencies, fieldDependencies } of calculationExecutionInfos) {
      result.calculationDependencies.push(...calculationDependencies)
      result.calculations.push(...calculations)
      result.configDependencies.push(...configDependencies)
      result.fieldDependencies.push(...fieldDependencies)
    }

    return result
  }

  // function for recursively getting all ancestor calculationKeys of specific calculationKey's calculation
  function getDependencyKeysOfCalculationKey(
    calculationKey: CalculationKey,
    dependencyConfig: CalculationDependencyConfig & Dependable
  ): CalculationExecutionInfos {
    const { dependants, dependsOn } = dependencyConfig[calculationKey]!

    const calculationExecutionInfos: CalculationExecutionInfos = {
      calculations: [calculationKey],
      calculationDependencies: dependsOn?.calculation ?? [],
      configDependencies: dependsOn?.config ?? [],
      fieldDependencies: dependsOn?.field ?? []
    }

    return combineCalculationExecutionInfos(
      calculationExecutionInfos,
      ...dependants.map((dependantKey) => getDependencyKeysOfCalculationKey(dependantKey, dependencyConfig))
    )
  }

  function sortingFunction(
    calculationKeyA: CalculationKey,
    calculationKeyB: CalculationKey,
    config: CalculationDependencyConfig & Dependable
  ) {
    if (calculationKeyA === calculationKeyB) return 0
    const dependantsOfA = config[calculationKeyA]!.dependants
    const dependenciesOfA = config[calculationKeyA]!.dependsOn?.calculation ?? []
    const fieldDependenciesOfA = config[calculationKeyA]!.dependsOn?.field ?? []

    const dependantsOfB = config[calculationKeyB]!.dependants
    const dependenciesOfB = config[calculationKeyB]!.dependsOn?.calculation ?? []
    const fieldDependenciesOfB = config[calculationKeyB]!.dependsOn?.field ?? []

    // If both have no dependencies, put A before B [A, B]
    if (!dependenciesOfA.length && !dependenciesOfB.length) {
      return 0
    }

    // If calculationB has dependency on calculationA or A has field dependencies and B hasn't, put A before B [A, B]
    if (
      dependantsOfA.includes(calculationKeyB) ||
      (fieldDependenciesOfA.length && !fieldDependenciesOfB.length) ||
      !dependenciesOfA.length
    ) {
      return -1
    }

    // If calculationA has dependency on calculationB put A after B [B, A]
    if (dependantsOfB.includes(calculationKeyA) || (fieldDependenciesOfB.length && !fieldDependenciesOfA.length) || !dependenciesOfB.length)
      return 1
    return 0
  }

  // function for sorting calculation dependants list so every calculation can is executed in correct order
  function sortFieldDependants(
    calculationDependants: CalculationKey[],
    config: CalculationDependencyConfig & Dependable
  ): CalculationKey[] {
    const result: CalculationKey[] = []

    for (let i = 0; i < calculationDependants.length; i++) {
      let insertIndex = 0
      const calculationKeyA = calculationDependants[i]

      for (let j = 0; j < result.length; j++) {
        const calculationKeyB = calculationDependants[j]

        const sortingOrder = sortingFunction(calculationKeyA, calculationKeyB, config)
        if (sortingOrder <= 0) continue
        if (calculationKeyA === calculationKeyB) continue
        insertIndex = insertIndex + 1
      }
      result.splice(insertIndex, 0, calculationKeyA)
    }

    return result
  }

  function getFieldDependants(fieldMappingKey: FormFieldMappingKey, config: CalculationDependencyConfig): CalculationKey[] {
    if (isNullish(config)) return []
    const configEntries = Object.entries(config!)
    return configEntries.flatMap(([calculationKey, calculationValue]) =>
      calculationValue.dependsOn?.field?.includes(fieldMappingKey) ? [calculationKey as CalculationKey] : []
    )
  }

  function getAllFieldDependants(config: CalculationDependencyConfig): CalculationKey[] {
    if (isNullish(config)) return []
    return Object.entries(config!).flatMap(([calculationKey, calculationValue]) =>
      calculationValue.dependsOn?.field?.length ? [calculationKey as CalculationKey] : []
    )
  }

  function handleCalculationError(e: Error, calculationKey: CalculationKey) {
    logWarn('CalculationService.executeCalculations', calculationKey, (e as Error).message)
    if (e instanceof BmfApiError) throw new CalculationError(calculationKey, 'bmf')
    throw new CalculationError(calculationKey, 'unknown')
  }

  // function for executing each calculation function after another and passing the intermediate results to the next
  async function executeCalculations(
    calculationKeys: CalculationKey[],
    calculationConfigs: CalculationDependencyConfig,
    externalDependencies: CalculationExternalDependencies
  ): Promise<CalculationServiceExecuteCalculationsResult> {
    let calculationFunctionParams: CalculationFunctionParams = convertExternalDependenciesToCalculationFunctionParams(externalDependencies)

    for (const calculationKey of calculationKeys) {
      const calculationConfig = calculationConfigs[calculationKey]! as CalculationDependencyConfigItem
      let calculationResult = null

      try {
        if (validateDependencies(calculationConfig.dependsOn, calculationFunctionParams, externalDependencies.fields))
          calculationResult = await executeCalculationFnOfCalculationKey(calculationKey, calculationFunctionParams)
      } catch (e) {
        handleCalculationError(e as Error, calculationKey as CalculationKey)
      }

      calculationFunctionParams = {
        ...calculationFunctionParams,
        calculationResults: {
          ...calculationFunctionParams.calculationResults,
          [calculationKey]: calculationResult
        }
      }
    }

    const { calculationResults } = calculationFunctionParams

    return { calculationResults }
  }

  function validateDependencies(
    dependencies: CalculationDependencies | null,
    calculationFunctionParams: CalculationFunctionParams,
    formFieldInformationList: OptionalFormFieldMappingRelated<FormFieldInformation>
  ): boolean {
    if (!dependencies) return true
    const { field, calculation, config } = dependencies
    const { fieldValues, calculationResults, config: configValue } = calculationFunctionParams
    const hasRequiredFieldValues =
      !field ||
      field.every((field) => {
        return formFieldInformationList[field]?.isDisabled || (isNotNullish(fieldValues[field]) && formFieldInformationList[field]?.isValid)
      })
    const hasRequiredCalculationResults = !calculation || calculation.every((calculation) => isNotNullish(calculationResults[calculation]))
    // @ts-ignore
    const hasRequiredConfig = !config || config.every((configKey) => configValue !== null && isNotNullish(configValue[configKey as string]))

    return hasRequiredFieldValues && hasRequiredCalculationResults && hasRequiredConfig
  }

  // function for getting needed calculation results from calculationStore
  function getRequiredCalculationResults(
    calculations: CalculationKey[],
    calculationDependencies: CalculationKey[]
  ): { [Key in CalculationKey]: number | string | null } {
    const calculationResults = useCalculationStore().calculatedResults
    return calculationDependencies.reduce((result, dependencyKey) => {
      if (calculations.includes(dependencyKey)) return result
      const [globalKey, innerKey] = dependencyKey.split('.')
      if (globalKey === 'leasing' || globalKey === 'private_buy')
        return {
          ...result,
          // @ts-ignore
          [dependencyKey]: calculationResults[globalKey][innerKey]
        }
      // @ts-ignore
      return { ...result, [dependencyKey]: calculationResults[globalKey] }
    }, {}) as { [Key in CalculationKey]: number | string | null }
  }

  // function for gathering dependency data, calculation results and passing least to patch function
  async function calculateFieldDependants(changedField: FormFieldMappingKey) {
    const CalculationStore = useCalculationStore()
    const currentResolvedCalculationConfig = toValue(calculationDependencyConfig)
    const config = CalculationStore.config
    if (!currentResolvedCalculationConfig || !config) return

    const fieldDependants = getFieldDependants(changedField, currentResolvedCalculationConfig)

    const { calculations, calculationDependencies, fieldDependencies } = combineCalculationExecutionInfos(
      ...fieldDependants.map((fieldDependant) => getDependencyKeysOfCalculationKey(fieldDependant, currentResolvedCalculationConfig))
    )
    const sortedCalculationDependants = sortFieldDependants([...new Set(calculations)], currentResolvedCalculationConfig)
    const requiredFields = await getRequiredFields([...new Set(fieldDependencies)])
    const requiredCalculationResults = getRequiredCalculationResults([...new Set(calculations)], [...new Set(calculationDependencies)])

    const calculationResults = await executeCalculations(sortedCalculationDependants, currentResolvedCalculationConfig, {
      calculations: requiredCalculationResults,
      fields: requiredFields,
      config
    })

    patchCalculationStore(calculationResults)
  }

  function convertExternalDependenciesToCalculationFunctionParams({
    calculations,
    fields,
    config
  }: CalculationExternalDependencies): CalculationFunctionParams {
    const fieldValues: OptionalFormFieldMappingRelated<FormFieldValues> = {}
    for (const key in fields) {
      fieldValues[key as FormFieldMappingKey] = fields[key as FormFieldMappingKey]?.value ?? null
    }
    return { calculationResults: calculations, fieldValues, config }
  }

  function handleTriggerCalculationError(e: Error, failedCalculationExecution: FormFieldMappingKey | null) {
    if (failedCalculationExecution) calculationQueue.unshift(failedCalculationExecution)
    currentCalculationExecution = null
    if (e instanceof CalculationError) {
      currentError = e
      useCalculationStore().updateCalculationErrors([e.i18n])
    } else throw e
  }

  // function for triggering calculation flow
  async function triggerCalculation(executeAll: boolean = false) {
    if ((!calculationQueue.length || !!currentCalculationExecution?.length) && !currentError && !executeAll) return

    const { updateLoadingState } = useGlobalStore()
    updateLoadingState(true)
    currentCalculationExecution = calculationQueue.shift()!
    try {
      if (currentError || executeAll) {
        const fieldChangesSinceLastSuccessfulCalculation = [...calculationQueue]
        await calculateAll()
        // remove all calculations which where executed by calculateAll()
        calculationQueue.splice(0, fieldChangesSinceLastSuccessfulCalculation.length)
      } else if (currentCalculationExecution) {
        await calculateFieldDependants(currentCalculationExecution)
      } else {
        logWarn(
          'CalculationService.triggerCalculation',
          'No calculations where executed, because neither the conditions for calculating all nor the for single calculation where valid'
        )
        currentCalculationExecution = null
        return
      }
      // Reset calculations
      currentError = null
      currentCalculationExecution = null
      useCalculationStore().updateCalculationErrors([])
      updateLoadingState(false)
      return triggerCalculation()
    } catch (e) {
      updateLoadingState(false)
      handleTriggerCalculationError(e as Error, currentCalculationExecution)
    }
  }

  // function for updating calculations which depend on a specific form field
  async function updateCalculations(changedField: FormFieldMappingKey): Promise<void> {
    calculationQueue.push(changedField)
    await triggerCalculation()
  }

  // function for execute all calculations
  async function updateAllCalculations(): Promise<void> {
    await triggerCalculation(true)
  }

  // function for getting required form field values
  async function getRequiredFields(requiredKeys: string[]): Promise<{ [Key in FormFieldMappingKey]?: FormFieldInformation }> {
    const MappingStore = useMappingStore()
    const CalculationStore = useCalculationStore()
    let requiredFields = {}
    for (const key of requiredKeys) {
      const fields: Array<FormField> = MappingStore.getFieldsOfKey('calculator', key).value
      if (fields.length) {
        const field = fields[fields.length - 1]
        const validationResult = await validateField(field)
        const isValid = typeof validationResult !== 'string' && validationResult
        const configDefaultValue = CalculationStore.config?.fields.find((configField) => configField.name === key)?.default
        const useConfigDefault = isNullish(field.config.value) && typeof configDefaultValue !== 'undefined'
        const isDisabled: boolean = field.config.disabled === true
        requiredFields = {
          ...requiredFields,
          [key]: {
            value: useConfigDefault ? configDefaultValue : (field.config.value as string | number | boolean | undefined),
            isValid: useConfigDefault || isValid,
            isDisabled
          }
        }
        continue
      }

      const configDefaultValue = CalculationStore.config?.fields.find((field) => field.name === key)?.default
      if (typeof configDefaultValue === 'undefined') continue

      requiredFields = {
        ...requiredFields,
        [key]: {
          value: configDefaultValue,
          // Fallback values from config are considered valid inputs for hidden/non-rendered fields.
          isValid: true,
          isDisabled: false
        }
      }
    }
    return { ...requiredFields }
  }

  // function for generating a patch object from calculation Results and calling CalculationStore.patchCalculations
  function patchCalculationStore({ calculationResults }: CalculationServicePatchInfo): void {
    const CalculationStore = useCalculationStore()
    const patch = Object.entries(calculationResults).reduce(
      (result, [calculationKey, calculationResult]) => {
        const [globalKey, innerKey] = calculationKey.split('.')
        if (globalKey && innerKey) {
          if (!result[globalKey]) {
            result[globalKey] = {}
          }
          // @ts-ignore
          result[globalKey][innerKey] = calculationResult
          return { ...result }
        }
        return { ...result, [globalKey]: calculationResult }
      },
      {} as { [key: string]: CalculationResultValue | { [key: string]: { [key: string]: CalculationResultValue } } }
    )

    CalculationStore.patchCalculations(patch)
  }

  // function for calculating everything in correct order
  async function calculateAll() {
    const CalculationStore = useCalculationStore()
    const currentResolvedCalculationConfig = toValue(calculationDependencyConfig)
    const config = CalculationStore.config
    if (!currentResolvedCalculationConfig || !config) return

    const calculationsBasedOnKeys: CalculationKey[] = getAllFieldDependants(currentResolvedCalculationConfig)

    const { calculations, fieldDependencies } = combineCalculationExecutionInfos(
      ...calculationsBasedOnKeys.map((calculationKey) =>
        getDependencyKeysOfCalculationKey(calculationKey, currentResolvedCalculationConfig)
      )
    )
    const sortedCalculationDependants = sortFieldDependants([...new Set(calculations)], currentResolvedCalculationConfig)

    const requiredFields = getRequiredFields([...new Set(fieldDependencies)])

    const calculationResults = await executeCalculations(sortedCalculationDependants, currentResolvedCalculationConfig, {
      calculations: {},
      fields: await requiredFields,
      config
    })

    patchCalculationStore(calculationResults)
  }

  return { updateCalculations, updateAllCalculations }
}

export const CalculationService = createCalculationService()
